import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const shortId = (id) => (id || "").slice(-6);

function StatusChip({ status }) {
  const styles =
    status === "pending"
      ? "text-amber-400 border-amber-500/30"
      : status === "executed"
        ? "text-emerald-400 border-emerald-500/30"
        : "text-[#94A3B8] border-[#222530]";
  return (
    <span className={`text-[10px] font-mono tracking-widest uppercase px-2 py-0.5 rounded-full border ${styles}`}>
      {status}
    </span>
  );
}

export default function AdminDeletionRequests() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback((s) => {
    setLoading(true);
    setError("");
    api
      .get("/admin/deletion-requests", { params: { status: s } })
      .then((res) => setItems(res.data))
      .catch((err) => {
        const code = err?.response?.status;
        setError(
          code === 401 || code === 403
            ? "You are not authorized to view deletion requests."
            : formatApiError(err),
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  return (
    <div className="space-y-6" data-testid="deletion-requests-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow-label mb-2">Admin</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Deletion Requests</h1>
          <p className="text-xs text-[#64748B] mt-2 max-w-xl leading-relaxed">
            Member-submitted account deletion requests. Read-only — requests stay pending unless a purge
            action is separately approved and executed. Financial records are retained in anonymised form.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(status)}
          className="btn-outline-gold px-4 py-2 text-xs flex items-center gap-2"
          data-testid="deletion-requests-refresh-button"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="flex gap-2" data-testid="deletion-requests-filters">
        {["pending", "all"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`px-4 py-1.5 text-xs font-mono tracking-widest uppercase rounded-full border transition-colors ${
              status === s ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" : "text-[#64748B] border-[#222530] hover:text-white"
            }`}
            data-testid={`deletion-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-[#64748B]" data-testid="deletion-requests-loading">Loading deletion requests...</p>
      ) : error ? (
        <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="deletion-requests-error">{error}</p>
      ) : items.length === 0 ? (
        <div className="card-lux p-8 text-center" data-testid="deletion-requests-empty">
          <p className="text-sm text-[#94A3B8]">No {status === "pending" ? "pending " : ""}deletion requests.</p>
        </div>
      ) : (
        <div className="card-lux divide-y divide-[#222530]" data-testid="deletion-requests-table">
          {items.map((r) => (
            <div key={r.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6" data-testid={`deletion-request-row-${shortId(r.id)}`}>
              <div className="sm:w-52 shrink-0">
                <p className="text-sm text-white font-mono tracking-wider" data-testid={`deletion-request-uid-${shortId(r.id)}`}>{r.member_uid || r.member?.uid || "—"}</p>
                <p className="text-xs text-[#64748B]">{r.member ? `${r.member.full_name || ""} · @${r.member.username}` : "member removed"}</p>
              </div>
              <div className="sm:w-28 shrink-0">
                <StatusChip status={r.status} />
              </div>
              <div className="flex-1 text-xs text-[#94A3B8] space-y-0.5">
                <p>Requested: <span className="text-white">{r.created_at ? fmtMYT(r.created_at) : "—"}</span></p>
                {r.executed_at && <p>Executed: <span className="text-white">{fmtMYT(r.executed_at)}</span></p>}
              </div>
              <div className="sm:w-44 shrink-0 text-right space-y-0.5">
                <p className="text-[10px] font-mono tracking-widest text-[#64748B] uppercase">Request #{shortId(r.id)}</p>
                {typeof r.coins_balance_minor === "number" && (
                  <p className="text-xs text-[#94A3B8]">COINS at request: <span className="text-white font-mono">{(r.coins_balance_minor / 100).toFixed(2)}</span></p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
